import {
  isjQuery,
  isBoolean,
  isFunction,
  isObject
} from './02.util.mjs';

import {
  kebabize,
  toArray,
  manageSpace
} from './03.helper.mjs';

/*==============================================================*/

/**
 * be or not
 */
jQuery.fn.be = function(not) {
  const nots = [false, 0, null, 'none', 'not'];
  const be = this.length > 0;
  return nots.includes(not) ? !be : be;
};

/**
 * value | vs jQuery.val()
 * @return whitespace managed string || this
 * * if value assigned, trigger event => change
 */
jQuery.fn.value = function(value = null, how = 'trim') {
  if ( value === null ) {
    const initialValue = this.val();
    return initialValue ? manageSpace(initialValue, how) : '';
  }

  // 🌟 [추가] API에서 넘어온 값이 숫자(우편번호 등)일 경우 대비 확실하게 String 캐스팅
  const safeValue = String(value);

  return manageSpace(safeValue, 'remove') === ''
    ? this.val('').removeAttr('value').trigger('change')
    : this.val( manageSpace(safeValue, how) ).trigger('change');
};

/**
 * status
 * * trigger event => propchange (상태가 실제로 변경되었을 때만)
 */
const _setStatus = function(status, value) {
  if ( !isBoolean(value) ) {
    if ( status === 'readonly' && this.is('select') ) {
      return this.attr('readonly') !== undefined;
    }
    return this.prop(status);
  }

  return this.each(function() {
    const $this = jQuery(this);

    const currentState = ( status === 'readonly' && $this.is('select') )
      ? $this.attr('readonly') !== undefined
      : $this.prop(status);

    if ( currentState !== value ) {
      if ( value ) {
        $this.prop(status, true);
        $this.attr(status, '');
      }
      else {
        $this.prop(status, false);
        $this.removeAttr(status);
      }

      $this.trigger('propchange', { prop: status, value });

      // [수정] 직접 만든 커스텀 익스텐션을 활용하여 코드를 우아하게 단축
      // value가 true면 -1이 전달되어 탭 인덱스가 설정되고,
      // value가 false면 빈 문자열('')이 전달되어 내부 로직에 의해 자동으로 속성이 제거(removeAttr)됩니다.
      if ( status === 'readonly' && ($this.is('select') || $this.is('[data-datetimer]')) ) {
        $this.tabindex(value ? -1 : '');
      }
    }
  });
};
jQuery.fn.checked = function(value = null) {
  return _setStatus.call(this, 'checked', value);
};
jQuery.fn.readonly = function(value = null) {
  return _setStatus.call(this, 'readonly', value);
};
jQuery.fn.disabled = function(value = null) {
  return _setStatus.call(this, 'disabled', value);
};
jQuery.fn.enabled = function(value = null) {
  // setter: .enabled(true) -> .disabled(false)
  // getter: .enabled() -> !.prop('disabled')
  if ( isBoolean(value) ) return _setStatus.call(this, 'disabled', !value);
  else !this.prop('disabled');
};
jQuery.fn.disable = function() {
  return _setStatus.call(this, 'disabled', true);
};
jQuery.fn.enable = function() {
  return _setStatus.call(this, 'disabled', false);
};

/**
 * prop | attr
 * @param {'prop'|'attr'} method
 * @param {string} name - 속성/프로퍼티의 이름
 * @param {string|number|null} value - 설정할 값 (null -> Getter 동작)
 * @param {'remove'|'trim'|'reduce'} how - 공백 처리 방식
 * @returns {jQuery|string|undefined}
 */
const _setPropAttr = function(method, name, value, how) {
  // getter
  if ( value === null ) return this[method](name) ?? undefined;

  // setter
  const managedValue = manageSpace(String(value), how);
  return managedValue === ''
    ? this.removeAttr(name) // 공백만 있으면 속성 제거
    : this[method](name, managedValue); // 값이 있으면 설정
};
jQuery.fn.id = function(value = null) {
  return _setPropAttr.call(this, 'prop', 'id', value, 'remove');
};
jQuery.fn.src = function(value = null) {
  // URL은 공백이 없어야 하므로 'remove' 사용
  return _setPropAttr.call(this, 'attr', 'src', value, 'remove');
};
jQuery.fn.alt = function(value = null) {
  // 대체 텍스트는 앞뒤 공백만 제거 ('trim')
  return _setPropAttr.call(this, 'attr', 'alt', value, 'trim');
};
jQuery.fn.href = function(value = null) {
  return _setPropAttr.call(this, 'attr', 'href', value, 'remove');
};
jQuery.fn.aria = function(name, value = null) {
  return _setPropAttr.call(this, 'attr', `aria-${name}`, value, 'remove');
};
jQuery.fn.type = function(value = null) {
  // type 속성에는 공백이 들어가면 안 되므로 'remove' 적용
  return _setPropAttr.call(this, 'attr', 'type', value, 'remove');
};
jQuery.fn.name = function(value = null) {
  return _setPropAttr.call(this, 'attr', 'name', value, 'remove');
};
jQuery.fn.lang = function(value = null) {
  return _setPropAttr.call(this, 'attr', 'lang', value, 'remove');
};
jQuery.fn.datetime = function(value = null) {
  return _setPropAttr.call(this, 'attr', 'datetime', value, 'remove');
};
jQuery.fn.tabindex = function(value = null) {
  return _setPropAttr.call(this, 'attr', 'tabindex', value, 'remove');
};
jQuery.fn.min = function(value = null) {
  return _setPropAttr.call(this, 'attr', 'min', value, 'remove');
};
jQuery.fn.max = function(value = null) {
  return _setPropAttr.call(this, 'attr', 'max', value, 'remove');
};
jQuery.fn.defaultValue = function(value = null) {
  // 기본값은 보통 앞뒤 여백만 제거하는 것이 안전하므로 'trim' 적용
  return _setPropAttr.call(this, 'prop', 'defaultValue', value, 'trim');
};

/**
 * open | hidden
 * @param {boolean} true or false
 * @returns {jQuery}
 */
jQuery.fn.open = function(tf) { return this.prop('open', tf); };
jQuery.fn.hidden = function(tf) { return this.prop('hidden', tf); };

/**
 * datattr
 * - setter only -> for getter, simply use jQuery.data()
 * @param {Object} Dataset - { camelCaseKey: value } no 'data-' prefix
 * @returns {jQuery}
 */
jQuery.fn.datattr = function(Dataset) {
  if ( !isObject( Dataset ) ) {
    throw new Error(`${Dataset} is not supported: use an object.`);
  }

  const Attrs = Object.entries(Dataset).reduce((acc, [key, value]) => {
    acc[`data-${kebabize(key)}`] = value;
    return acc;
  }, {});

  return this.attr(Attrs).data(Dataset);
};

/**
 * 하나 이상의 데이터 속성 제거
 * @param {string|Array} [keys] - 제거할 키 (생략 시 모든 데이터 속성 제거)
 * @ - [key1, key2] | 'key1 key2' | 'key1, key2'
 * @returns {jQuery}
 */
jQuery.fn.removeDatattr = function(keys) {
  const delKeys = toArray(keys ?? Object.keys(this.data() ?? {}));
  if ( delKeys.length === 0 ) return this;
  const delAttrs = delKeys.map(key => `data-${kebabize(key)}`).join(' ');
  return this.removeData(keys).removeAttr(delAttrs);
};

/**
 * 조건에 따라 데이터 속성 토글
 * @param {string} key - 데이터 속성의 키 (camelCase)
 * @param {boolean} condition - 토글 조건
 * @param {Array} [values=null] - [참일 때 값, 거짓일 때 값]
 * @returns {jQuery}
 */
jQuery.fn.toggleDatattrIf = function(key, condition, values = null) {
  // values 배열이면, condition 따라 해당 값을 설정
  if ( Array.isArray(values) ) {
    return this.datattr({ [key]: values[condition ? 0 : 1] });
  }
  // values 없으면, condition 참일 때 빈 값 속성 추가하고, 거짓일 때 속성 제거
  return condition ? this.datattr({ [key]: '' }) : this.removeDatattr(key);
};

/**
 * 조건에 따라 TF 깂을 데이터 속성으로 토글
 */
jQuery.fn.toggleBooleanDatattrIf = function(key, condition) {
  return this.toggleDatattrIf(key, condition, [true, false]);
};

/**
 * have = has + be | haveNot = has + be(not) | isEmpty = is(:empty)
 * @return have|haveNot() => true || false
 * @return hasNot() => $elements
 * * note that jQuery.has() returns '$elements'
 */
jQuery.fn.have = function(selector) {
  return this.has(selector).be();
};
jQuery.fn.haveNot = function(selector) {
  return this.has(selector).be('not');
};
jQuery.fn.hasNot = function(selector) {
  return this.filter(`:not(:has(${selector}))`);
};
jQuery.fn.isEmpty = function() {
  return this.is(':empty');
};

/**
 * jQuery.css() 통해 설정된 속성을 제거
 * @param {string|Array<string>} props - 제거할 CSS 속성 이름들
 * @returns {jQuery}
 */
jQuery.fn.removeCss = function(props) {
  const delProps = toArray(props).reduce((acc, prop) => {
    acc[prop] = ''; // 값을 빈 문자열로 설정하여 CSS 속성 제거
    return acc;
  }, {});
  return this.css(delProps);
};

/**
 * 조건에 따라 CSS 속성들을 적용하거나 제거
 * @param {Object} Props - 적용할 CSS 속성 객체 { prop: value }
 * @param {boolean} condition - 토글 조건
 * @returns {jQuery}
 */
jQuery.fn.toggleCssIf = function(Props, condition) {
  return condition ? this.css(Props) : this.removeCss(Object.keys(Props));
};

/**
 * CSS 속성 값을 정수로 반환
 * @param {string} prop - 가져올 CSS 속성 이름
 * @returns {number} 변환된 정수 값 (실패 시 0)
 */
jQuery.fn.cssInt = function(prop) {
  return parseInt(this.css(prop), 10) || 0;
};

/**
 * CSS 속성 값을 부동 소수점 수로 반환
 * @param {string} prop - 가져올 CSS 속성 이름
 * @returns {number} 변환된 값 (실패 시 0)
 */
jQuery.fn.cssFloat = function(prop) {
  return parseFloat(this.css(prop)) || 0;
};

/**
 * fadeIn 효과와 함께 DOM 요소를 추가
 * @param {'insertAfter'|'appendTo'|'prependTo'} where - 삽입 위치
 * @param {string|jQuery} html - 삽입할 HTML 또는 jQuery 객체
 * @returns {jQuery}
 */
const _setFadeIn = function(where, html, speed, callback) {
  // html === jQuery 객체면 그대로 사용, 아니면 생성
  const $html = isjQuery(html)
    ? html : jQuery(jQuery.parseHTML(String(html).trim()));
  // 체이닝을 위해 this 반환하도록 수정
  return $html.hide()[where](this).fadeIn(speed, function() {
    callback?.(); // 옵셔널 체이닝 : 콜백이 함수일 때만 호출
  });
};
jQuery.fn.fadeInAfter = function(html, speed = 400, callback = null) {
  return _setFadeIn.call(this, 'insertAfter', html, speed, callback);
};
jQuery.fn.fadeInAppend = function(html, speed = 400, callback = null) {
  return _setFadeIn.call(this, 'appendTo', html, speed, callback);
};
jQuery.fn.fadeInPrepend = function(html, speed = 400, callback = null) {
  return _setFadeIn.call(this, 'prependTo', html, speed, callback);
};

/**
 * fadeOut 후 요소 제거
 * @returns {jQuery}
 * * jQuery.fadeOut() 함수는 원래 jQuery 객체 반환하므로 체이닝이 가능
 */
jQuery.fn.fadeOutRemove = function(speed = 400, callback = null) {
  return this.fadeOut(speed, function() {
    jQuery(this).remove(); // this = 애니메이션이 완료된 요소
    callback?.();
  });
};

/**
 * smooth scroll into view
 * @param {Object} options
 * @param {string} [options.block='nearest'] - start|center|end
 * @param {string} [options.inline='nearest'] - start|center|end
 * @param {number} [options.delay=0] - 지연 시간 (ms)
 * @returns {jQuery}
 */
jQuery.fn.smartScroll = function(
  { block = 'nearest', inline = 'nearest', delay = 0 } = {}
) {
  const elem = this[0];
  if ( !elem ) return this;

  const execute = () => {
    // ? 최신 브라우저 지원 (Safari 등 일부 구형 브라우저 대비 try-catch 가능)
    try {
      elem.scrollIntoView({ behavior: 'smooth', block, inline });
    }
    catch (e) { // fallback : 구형 브라우저나 옵션 미지원 시
      elem.scrollIntoView();
    }
  };

  if ( delay > 0 ) setTimeout(execute, delay);
  else execute();

  return this;
};

/**
 * number counting animation
 * @param {number} to - The number to count to
 * @param {number} duration - Animation duration in ms
 * @returns {jQuery}
 */
jQuery.fn.countTo = function(to, duration = 400) {
  return this.each(function() {
    const $this = jQuery(this);
    const from = parseInt($this.text(), 10) || 0;
    const till = parseInt(to, 10) || 0;

    if ( from === till ) {
      $this.text(till);
      return;
    }

    jQuery({ counter: from }).animate({ counter: till }, {
      duration: duration,
      easing: 'swing',
      step: function() { $this.text(Math.ceil(this.counter)); },
      complete: () => $this.text(till) // 오차 방지용 최종값 삽입
    });
  });
};

/**
 * rollback to initial snapshot
 * DOM 속성(Attributes)과 내부 캐시 데이터를 렌더링 직후의 초기 상태로 완벽 복원
 * @returns {jQuery}
 */
jQuery.fn.rollback = function() {
  return this.each(function() {
    if ( this.__initialAttrs ) {
      // 1. 현재 속성 중 원본에 없는 것(JS가 동적 추가한 찌꺼기) 전부 삭제
      Array.from(this.attributes).forEach(attr => {
        if ( !this.__initialAttrs.has(attr.name) ) {
          this.removeAttribute(attr.name);
        }
      });
      // 2. 원본 속성(하드코딩 값) 덮어쓰기 복구
      this.__initialAttrs.forEach((value, name) => {
        this.setAttribute(name, value);
      });
      // 3. jQuery 내부 data 캐시 메모리 완전 초기화
      jQuery(this).removeData();
    }
  });
};